import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import {
  DataGrid,
  type GridColDef,
  type GridRenderCellParams,
  type GridValidRowModel,
} from "@mui/x-data-grid";
import { dataGridSx, dataGridWrapperSx } from "@components/DataTable/styles";
import OverflowTooltip from "@components/OverflowTooltip/OverflowTooltip";

/** Same overflow treatment as the shared table cells: ellipsis, full text in a tooltip. */
const CellOverflow: React.FC<{ children: React.ReactNode; fallbackTitle?: string }> = ({
  children,
  fallbackTitle,
}) => (
  <OverflowTooltip
    placement="top"
    title={(el) => el?.textContent?.trim() || fallbackTitle || ""}
    getTarget={(wrapperEl) => {
      const target = wrapperEl.querySelector(
        "[data-overflow-tooltip-target='true']",
      ) as HTMLElement | null;
      return (target?.firstElementChild as HTMLElement | null) || target;
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", width: "100%", minWidth: 0, height: "100%" }}>
      <Box
        data-overflow-tooltip-target="true"
        sx={{
          "display": "block",
          "width": "100%",
          "minWidth": 0,
          "overflow": "hidden",
          "textOverflow": "ellipsis",
          "whiteSpace": "nowrap",
          "& > *": { maxWidth: "100%" },
        }}
      >
        {children}
      </Box>
    </Box>
  </OverflowTooltip>
);

const gridSx = [
  dataGridSx,
  {
    "& .MuiDataGrid-row:hover": { cursor: "default" },
    "& .MuiDataGrid-cell": { px: 1, fontSize: 13 },
    "& .MuiDataGrid-columnHeader": { px: 1 },
    "& .MuiDataGrid-columnHeaderTitle": {
      whiteSpace: "normal",
      lineHeight: 1.2,
      fontSize: 13,
    },
  },
] as const;

/** Wraps every cell in the overflow treatment, except the ones that render their own control. */
const withOverflow = (column: GridColDef, plainFields: string[]): GridColDef => {
  if (plainFields.includes(column.field)) {
    return { ...column, display: "flex" };
  }
  const original = column.renderCell;
  return {
    ...column,
    display: "flex",
    renderCell: (params: GridRenderCellParams) => (
      <CellOverflow fallbackTitle={String(params.value ?? "")}>
        {original ? original(params) : (params.value ?? "-")}
      </CellOverflow>
    ),
  };
};

/**
 * DBCO-0029 - a feature-local, read-only grid on MUI X `DataGrid` with the platform's shared grid styling
 * (`dataGridSx`). It is NOT `CustomDataTable`: that one forces every text column to at least 150px (so
 * eleven columns scroll sideways) and always renders a paging footer, neither of which the wireframe has.
 * All rows are shown (no footer, no paging), headers wrap, and a one-line message replaces the empty state.
 * Virtualization is off so every row and column is in the DOM.
 */
const OperationDataGrid: React.FC<{
  title: string;
  hideTitle?: boolean;
  testId: string;
  columns: GridColDef[];
  /** Fields whose `renderCell` draws its own control (button, menu), left out of the ellipsis wrapper. */
  plainFields?: string[];
  rows: GridValidRowModel[];
  emptyMessage: string;
  /** DBCO-0062: a row opens its detail; the row then shows a pointer. */
  onRowOpen?: (id: string) => void;
}> = ({ title, hideTitle, testId, columns, plainFields = [], rows, emptyMessage, onRowOpen }) => {
  const wrapped = useMemo(
    () =>
      columns.map((column) =>
        withOverflow({ sortable: false, filterable: false, ...column }, plainFields),
      ),
    [columns, plainFields],
  );

  return (
    <section data-testid={`${testId}-section`} className="flex flex-col gap-2">
      {!hideTitle && (
        <Typography variant="h6" data-testid={`${testId}-title`}>
          {title}
        </Typography>
      )}
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" data-testid={`${testId}-empty`}>
          {emptyMessage}
        </Typography>
      ) : (
        <Box sx={dataGridWrapperSx} data-testid={testId}>
          <DataGrid
            rows={rows}
            columns={wrapped}
            autoHeight
            hideFooter
            disableColumnMenu
            disableColumnSorting
            disableColumnResize
            disableRowSelectionOnClick
            disableVirtualization
            columnHeaderHeight={52}
            getRowId={(row) => row.id}
            onRowClick={onRowOpen ? (params) => onRowOpen(String(params.id)) : undefined}
            sx={
              (onRowOpen
                ? [...gridSx, { "& .MuiDataGrid-row:hover": { cursor: "pointer" } }]
                : gridSx) as never
            }
          />
        </Box>
      )}
    </section>
  );
};

export default OperationDataGrid;
